import { createPatchData } from 'features/fde'
import { collectDefaultsForFields } from 'features/libra/ontology/ontologyFieldDefaults'
import {
  FieldValuesSchema,
  OntologyField,
} from 'features/libra/ontology/ontologyFieldTypes'
import { FormLayoutItem } from 'features/libra/sections/formLayout'
import { Section, SectionType } from 'features/libra/sections/sectionTypes'
import { getVisibleSectionIds } from 'features/libra/stepComponents/conditionalForm/conditionalFormSection/conditionalFormVisibility'
import { useTicketUpdate } from 'features/libra/ticket/TicketUpdateContext'
import { useCallback, useEffect, useMemo, useState } from 'react'

type FieldValuesType = Record<string, unknown>

type UseWorkflowFieldValuesOptions = {
  ontologyFields: Record<string, OntologyField>
  sections: ReadonlyArray<Section>
  form: ReadonlyArray<FormLayoutItem>
}

export const useWorkflowFieldValues = ({
  ontologyFields,
  sections,
  form,
}: UseWorkflowFieldValuesOptions) => {
  const {
    fieldValues: ticketFieldValues,
    setFieldValues: saveFieldValues,
    isLoading,
  } = useTicketUpdate()

  const initialFieldValues = useMemo<FieldValuesType>(
    () => FieldValuesSchema.parse(ticketFieldValues ?? {}),
    [ticketFieldValues]
  )

  const [localFieldValues, setLocalFieldValues] =
    useState<FieldValuesType>(initialFieldValues)

  // Update the local field values if the ticket values change under our feet
  useEffect(() => {
    setLocalFieldValues(initialFieldValues)
  }, [initialFieldValues])

  const normalizedFieldValues = useMemo<FieldValuesType>(
    () => localFieldValues ?? {},
    [localFieldValues]
  )

  // Get all visible section IDs
  const visibleSectionIds = useMemo(
    () => getVisibleSectionIds(form, normalizedFieldValues),
    [form, normalizedFieldValues]
  )

  // Get all visible sections (both regular and flowchart)
  const allVisibleSections = useMemo(
    () =>
      visibleSectionIds
        .map((sectionId) =>
          sections.find((section) => section.id === sectionId)
        )
        .filter((section): section is Section => section !== undefined),
    [visibleSectionIds, sections]
  )

  // Get fields from form sections only
  const visibleFields = useMemo(
    () =>
      allVisibleSections
        .filter((section) => section.type === SectionType.FORM)
        .flatMap((section) => section.fields),
    [allVisibleSections]
  )

  const defaultsForVisibleFields = useMemo(
    () =>
      collectDefaultsForFields(
        normalizedFieldValues,
        ontologyFields,
        visibleFields
      ),
    [normalizedFieldValues, ontologyFields, visibleFields]
  )

  const fieldValuesWithDefaults = useMemo<FieldValuesType>(() => {
    const merged = {
      ...normalizedFieldValues,
      ...defaultsForVisibleFields,
    }
    return Object.fromEntries(
      Object.entries(merged).filter(([, value]) => value !== undefined)
    ) as FieldValuesType
  }, [normalizedFieldValues, defaultsForVisibleFields])

  const hasChanges = useMemo(
    () =>
      createPatchData('id', initialFieldValues, fieldValuesWithDefaults) !==
      undefined,
    [fieldValuesWithDefaults, initialFieldValues]
  )

  const handleCancel = useCallback(() => {
    setLocalFieldValues(initialFieldValues)
  }, [initialFieldValues])

  const handleSave = useCallback(async () => {
    await saveFieldValues(fieldValuesWithDefaults)
  }, [fieldValuesWithDefaults, saveFieldValues])

  return {
    fieldValues: localFieldValues,
    fieldValuesWithDefaults,
    initialFieldValues,
    setFieldValues: setLocalFieldValues,
    allVisibleSections,
    hasChanges,
    handleCancel,
    handleSave,
    isLoading,
  }
}

export type UseWorkflowFieldValuesReturn = ReturnType<
  typeof useWorkflowFieldValues
>
